import { AccessUser } from 'domain/access-user';
import { BranchOffices } from 'domain/branch-offices';
import { Login } from 'domain/login';
import produce, { Draft } from 'immer';
import { ModalDialogueProps } from 'shared/components/ModalDialogue/ModalDialogue';
import { ErrorPageProps } from 'shared/domain/error-page';
import { DispatchObject } from 'shared/utils/dispatch-object';
import { ErrorDelivery } from 'shared/utils/error-delivery';
import { GlobalAction } from './global-actions';

export interface GlobalState {
  isFetching: boolean;
  pendingFetches: number;
  error: Readonly<ErrorDelivery<ErrorPageProps | ModalDialogueProps>> | null;
  modalAlert: ModalDialogueProps | null;
  login: Login | null;
  accessUser: AccessUser | null;
  branchOfficeSelected: BranchOffices | null;
  isImpersonating: boolean;
  impersonatingAs: string | null;
}

export const initialState: GlobalState = {
  isFetching: false,
  pendingFetches: 0,
  error: null,
  modalAlert: null,
  login: null,
  accessUser: null,
  branchOfficeSelected: null,
  isImpersonating: false,
  impersonatingAs: null,
};

export const globalContextReducer = produce((draft: Draft<GlobalState>, { type, payload }: DispatchObject<GlobalAction>) => {
  switch (type) {
    case GlobalAction.StartFetching:
      draft.pendingFetches += 1;
      draft.isFetching = true;
      draft.error = null;
      break;

    case GlobalAction.FinishedFetching:
      draft.pendingFetches = Math.max(draft.pendingFetches - 1, 0);
      if (draft.pendingFetches <= 0) {
        draft.isFetching = false;
      }
      break;

    case GlobalAction.SetError:
      draft.error = payload;
      break;

    case GlobalAction.SetModalAlert:
      draft.modalAlert = payload;
      break;

    case GlobalAction.LoginInfo:
      draft.login = payload;
      break;

    case GlobalAction.AccessUser:
      draft.accessUser = payload;
      break;

    case GlobalAction.BranchOfficeSelected:
      draft.branchOfficeSelected = payload;
      break;

    case GlobalAction.ImpersonationStarted:
      draft.isImpersonating = true;
      draft.impersonatingAs = payload;
      break;

    case GlobalAction.ImpersonationEnded:
      draft.isImpersonating = false;
      draft.impersonatingAs = null;
      break;

    case GlobalAction.ClearState:
      return initialState;

    default:
      throw new Error(`Unknown global type: ${type}`);
  }
});
